import type { Point } from '@/domain/world/geometry';
import { placeInRegion } from '@/domain/world/placement';
import type { RegionDefinition, RegionZoneKind } from '@/domain/world/region-definition';

export type EnemyPlacementSpec =
  | { kind: 'fauna'; rank: 'common'; seed: string }
  | { kind: 'wild'; rank: 'common' | 'dominant'; seed: string };

export interface PlacedEnemySpec<T extends EnemyPlacementSpec = EnemyPlacementSpec> {
  spec: T;
  point: Point;
}

function placementRule(spec: EnemyPlacementSpec): {
  zoneKinds: readonly RegionZoneKind[];
  radius: number;
} {
  if (spec.kind === 'fauna') return { zoneKinds: ['fauna'], radius: 22 };
  return spec.rank === 'dominant'
    ? { zoneKinds: ['dominant'], radius: 32 }
    : { zoneKinds: ['wild'], radius: 26 };
}

export function placeEnemySpec<T extends EnemyPlacementSpec>(
  region: RegionDefinition,
  spec: T,
  occupied: readonly Point[],
): PlacedEnemySpec<T> | null {
  const rule = placementRule(spec);
  let result = placeInRegion({
    region,
    seed: spec.seed,
    zoneKinds: rule.zoneKinds,
    radius: rule.radius,
    occupied,
    // placeInRegion soma o raio ao clearance; isto preserva 96 unidades
    // centro-a-centro sem reduzir desnecessariamente a capacidade das zonas.
    minSeparation: 96 - rule.radius,
    avoidEntrancesBy: 96,
    maxAttempts: 96,
  });

  // Uma amostra aleatória pode cair num empacotamento ruim mesmo quando a
  // zona ainda possui espaço. Mantém a distribuição determinística, mas
  // percorre sub-sementes antes de desistir de um dos seis spawns da run.
  for (let retry = 1; !result.ok && result.reason === 'no-valid-point' && retry <= 16; retry += 1) {
    result = placeInRegion({
      region,
      seed: `${spec.seed}:retry:${retry}`,
      zoneKinds: rule.zoneKinds,
      radius: rule.radius,
      occupied,
      minSeparation: 96 - rule.radius,
      avoidEntrancesBy: 96,
      maxAttempts: 128,
    });
  }

  if (
    !result.ok &&
    result.reason === 'missing-zone' &&
    spec.kind === 'wild' &&
    spec.rank === 'dominant'
  ) {
    result = placeInRegion({
      region,
      seed: spec.seed,
      zoneKinds: ['wild'],
      radius: rule.radius,
      occupied,
      minSeparation: 96 - rule.radius,
      avoidEntrancesBy: 96,
      maxAttempts: 96,
    });
  }

  return result.ok ? { spec, point: result.point } : null;
}

export function placeEnemySpecs<T extends EnemyPlacementSpec>(
  region: RegionDefinition,
  specs: readonly T[],
): Array<PlacedEnemySpec<T>> {
  const placed: Array<PlacedEnemySpec<T>> = [];
  for (const spec of specs) {
    const result = placeEnemySpec(
      region,
      spec,
      placed.map(({ point }) => point),
    );
    if (result) placed.push(result);
  }
  return placed;
}
